import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import ImageUploader from "@/components/ImageUploader";
import CustomDomainManager from "@/components/CustomDomainManager";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Save } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, value, onChange, type = "text", placeholder = "", testid }) => (
  <div>
    <label className="overline text-muted-foreground mb-1.5 block">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-10 px-3 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-sm"
    />
  </div>
);

const ColorPicker = ({ label, value, onChange, testid, presets = [] }) => (
  <div>
    <label className="overline text-muted-foreground mb-1.5 block">{label}</label>
    <div className="flex items-center gap-2">
      <input data-testid={`${testid}-color`} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded-sm border border-border cursor-pointer" />
      <input data-testid={testid} type="text" value={value} onChange={(e) => onChange(e.target.value)} className="flex-1 h-10 px-3 rounded-sm border border-border text-sm font-mono" placeholder="#000000" />
    </div>
    <div className="flex gap-1.5 mt-2 flex-wrap">
      {presets.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)} data-testid={`${testid}-preset-${c.replace("#", "")}`}
          className={`w-7 h-7 rounded-sm border-2 ${value?.toLowerCase() === c.toLowerCase() ? "border-foreground" : "border-transparent"} hover:scale-110 transition-transform`}
          style={{ background: c }} title={c} />
      ))}
    </div>
  </div>
);

const GOOGLE_FONTS = [
  "Cabinet Grotesk", "General Sans", "Inter", "Roboto", "Open Sans", "Lato", "Montserrat", "Poppins",
  "Playfair Display", "Cormorant Garamond", "Cormorant", "DM Serif Display", "EB Garamond", "Lora",
  "Bodoni Moda", "Fraunces", "PT Serif", "Merriweather", "Crimson Text",
  "Bebas Neue", "Oswald", "Raleway", "Quicksand", "Work Sans", "Manrope", "DM Sans", "Space Grotesk",
  "Plus Jakarta Sans", "Outfit", "Sora", "Archivo", "Karla",
];

const FontPicker = ({ label, value, onChange, testid }) => (
  <div>
    <label className="overline text-muted-foreground mb-1.5 block">{label}</label>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full h-10 px-3 rounded-sm border border-border bg-white text-sm">
      {GOOGLE_FONTS.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
    </select>
    <div style={{ fontFamily: value }} className="mt-2 text-lg" data-testid={`${testid}-preview`}>The quick brown fox · 0123456789</div>
  </div>
);

export default function TenantSettings() {
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("brand");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then((r) => {
      const s = r.data.settings || {};
      setTenant({
        ...r.data,
        settings: {
          brand_kit: s.brand_kit || {},
          payment_config: s.payment_config || { stripe: {}, paypal: {}, klarna: {} },
          email_config: s.email_config || { resend: {} },
          shipping_config: s.shipping_config || { carriers: [], rates: [], free_shipping_above: 0 },
          legal_info: s.legal_info || {},
          social_links: s.social_links || {},
        },
      });
    });
  }, []);

  if (!tenant) return <AdminLayout><div className="p-10">Loading…</div></AdminLayout>;

  const s = tenant.settings;
  const setBrand = (key, val) => setTenant({ ...tenant, settings: { ...s, brand_kit: { ...s.brand_kit, [key]: val } } });
  const setSocial = (key, val) => setTenant({ ...tenant, settings: { ...s, social_links: { ...s.social_links, [key]: val } } });
  const setLegal = (key, val) => setTenant({ ...tenant, settings: { ...s, legal_info: { ...s.legal_info, [key]: val } } });
  const setPayment = (provider, key, val) => setTenant({
    ...tenant, settings: { ...s, payment_config: { ...s.payment_config, [provider]: { ...s.payment_config[provider], [key]: val } } }
  });
  const setEmail = (provider, key, val) => setTenant({
    ...tenant, settings: { ...s, email_config: { ...s.email_config, [provider]: { ...s.email_config[provider], [key]: val } } }
  });
  const setShipping = (key, val) => setTenant({ ...tenant, settings: { ...s, shipping_config: { ...s.shipping_config, [key]: val } } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/me/settings", s);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10 max-w-5xl" data-testid="tenant-settings-page">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-brand mb-2">{tenant.type === "casa_madre" ? "Casa Madre" : "Rivenditore"} · {tenant.name}</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">Impostazioni</h1>
          </div>
          <button data-testid="settings-save-btn" onClick={save} disabled={saving}
            className="px-5 h-11 inline-flex items-center gap-2 rounded-sm bg-brand hover:bg-brand-hover text-white text-sm font-medium transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "..." : "Salva tutto"}
          </button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="settings-tabs" className="flex-wrap">
            <TabsTrigger value="brand" data-testid="tab-brand">Brand</TabsTrigger>
            <TabsTrigger value="template" data-testid="tab-template">Template sito</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Pagamenti</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping">Spedizioni</TabsTrigger>
            <TabsTrigger value="legal" data-testid="tab-legal">Legal & GDPR</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social">Social</TabsTrigger>
            <TabsTrigger value="domain" data-testid="tab-domain">Dominio</TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="mt-6 bg-white border border-border rounded-sm p-6">
            <p className="text-sm text-muted-foreground mb-5">Scegli il layout della tua vetrina. Puoi cambiarlo in qualsiasi momento.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                { key: "minimal", title: "Minimal", desc: "Layout pulito, focus prodotto. Adatto per moda, design, brand contemporanei.", img: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=900" },
                { key: "editorial", title: "Editorial Furniture", desc: "Layout editoriale con hero scuro, ambientazioni, filtri laterali. Ideale per arredamento di lusso, showroom.", img: "https://images.unsplash.com/photo-1618219740975-d40978bb7378?w=900" },
              ].map((tpl) => {
                const active = (s.brand_kit.template || "minimal") === tpl.key;
                return (
                  <button key={tpl.key} type="button" data-testid={`template-${tpl.key}`} onClick={() => setBrand("template", tpl.key)}
                    className={`text-left border-2 rounded-sm overflow-hidden transition-all ${active ? "border-brand" : "border-border hover:border-brand/40"}`}>
                    <div className="aspect-[16/9] bg-neutral-100 overflow-hidden">
                      <img src={tpl.img} alt={tpl.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-heading font-bold">{tpl.title}</div>
                        {active && <span className="text-xs text-brand font-medium">✓ Selezionato</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{tpl.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="brand" className="mt-6 space-y-5 bg-white border border-border rounded-sm p-6">
            {/* Template selector */}
            <div>
              <label className="overline text-muted-foreground mb-2 block">Template vetrina</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { key: "minimal", title: "Minimal", desc: "Pulito, focus prodotto, fondo bianco" },
                  { key: "editorial", title: "Editorial", desc: "Lifestyle, hero, blocchi narrativi" },
                  { key: "editorial-luxe", title: "Editorial Luxe", desc: "Premium · scuro · parallax · serif XL", badge: "NEW" },
                ].map((t) => {
                  const active = (s.brand_kit.template || "minimal") === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => setBrand("template", t.key)}
                      data-testid={`template-${t.key}`}
                      className={`p-4 rounded-sm border text-left transition-all ${active ? "border-brand bg-brand/5 ring-2 ring-brand/30" : "border-border hover:border-foreground"}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-heading font-bold">{t.title}</span>
                        {t.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-foreground text-white font-bold tracking-wider">{t.badge}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{t.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Public vetrina toggle */}
            <div className="flex items-center justify-between p-4 rounded-sm border border-border" data-testid="vetrina-public-toggle">
              <div>
                <div className="font-medium">Vetrina pubblica</div>
                <div className="text-xs text-muted-foreground">Se disattivata, il tuo storefront non è accessibile pubblicamente (utile per Casa Madre che opera in B2B con possibili concorrenti).</div>
              </div>
              <Switch checked={s.brand_kit.vetrina_public !== false} onCheckedChange={(v) => setBrand("vetrina_public", v)} data-testid="brand-vetrina-public" />
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <Field label="Nome attività" value={s.brand_kit.display_name || tenant.name} onChange={(v) => setBrand("display_name", v)} testid="brand-display-name" />
              <Field label="Tagline" value={s.brand_kit.tagline} onChange={(v) => setBrand("tagline", v)} testid="brand-tagline" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <ColorPicker label="Colore primario" value={s.brand_kit.primary_color || "#1a1a1a"} onChange={(v) => setBrand("primary_color", v)} testid="brand-primary" presets={["#0F1B14", "#1a1a1a", "#C25934", "#C6A664", "#1d4ed8", "#dc2626", "#059669", "#7c3aed"]} />
              <ColorPicker label="Colore secondario / accento" value={s.brand_kit.secondary_color || "#C25934"} onChange={(v) => setBrand("secondary_color", v)} testid="brand-secondary" presets={["#C6A664", "#C25934", "#f59e0b", "#ec4899", "#8b5cf6", "#10b981", "#0ea5e9", "#6366f1"]} />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <FontPicker label="Font heading" value={s.brand_kit.font_heading || "Cabinet Grotesk"} onChange={(v) => setBrand("font_heading", v)} testid="brand-font-heading" />
              <FontPicker label="Font body" value={s.brand_kit.font_body || "General Sans"} onChange={(v) => setBrand("font_body", v)} testid="brand-font-body" />
            </div>
            <div className="p-4 rounded-sm border border-border bg-neutral-50" data-testid="brand-preview">
              <div className="overline text-muted-foreground mb-2">Anteprima live</div>
              <div style={{ fontFamily: s.brand_kit.font_heading || "Cabinet Grotesk", color: s.brand_kit.primary_color || "#1a1a1a" }} className="font-bold text-3xl mb-1">
                {s.brand_kit.display_name || tenant.name}
              </div>
              <div style={{ fontFamily: s.brand_kit.font_body || "General Sans", color: s.brand_kit.secondary_color || "#C25934" }} className="text-sm">
                {s.brand_kit.tagline || "La tua tagline qui"}
              </div>
              <button type="button" style={{ background: s.brand_kit.primary_color || "#1a1a1a", fontFamily: s.brand_kit.font_body || "General Sans" }} className="mt-3 px-4 h-9 text-white text-sm rounded-sm">
                Bottone CTA
              </button>
            </div>
            <ImageUploader value={s.brand_kit.logo_url} onChange={(v) => setBrand("logo_url", v)} label="Logo" testid="brand-logo" />
            <ImageUploader value={s.brand_kit.favicon_url} onChange={(v) => setBrand("favicon_url", v)} label="Favicon" testid="brand-favicon" />
            <ImageUploader value={s.brand_kit.hero_image} onChange={(v) => setBrand("hero_image", v)} label="Immagine hero default" testid="brand-hero" />
            {tenant.type === "casa_madre" && (
              <NetworkCoverEditor networkId={tenant.network_id} />
            )}
            <div>
              <label className="overline text-muted-foreground mb-1.5 block">Testo footer</label>
              <textarea data-testid="brand-footer-text" value={s.brand_kit.footer_text || ""} onChange={(e) => setBrand("footer_text", e.target.value)}
                rows={3} className="w-full px-3 py-2 rounded-sm border border-border text-sm"
                placeholder="Indirizzo, P.IVA, contatti…" />
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-6 space-y-6">
            {[
              { key: "stripe", title: "Stripe", fields: ["publishable_key", "secret_key", "webhook_secret"] },
              { key: "paypal", title: "PayPal", fields: ["client_id", "secret", "mode"] },
              { key: "klarna", title: "Klarna", fields: ["username", "password", "region"] },
            ].map((prov) => (
              <div key={prov.key} className="bg-white border border-border rounded-sm p-6" data-testid={`payment-${prov.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg">{prov.title}</h3>
                  <Switch
                    data-testid={`payment-${prov.key}-enabled`}
                    checked={!!s.payment_config[prov.key]?.enabled}
                    onCheckedChange={(v) => setPayment(prov.key, "enabled", v)}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {prov.fields.map((f) => (
                    <Field key={f} label={f.replace(/_/g, " ")} value={s.payment_config[prov.key]?.[f]}
                      onChange={(v) => setPayment(prov.key, f, v)} testid={`payment-${prov.key}-${f}`}
                      type={f.includes("secret") || f === "password" ? "password" : "text"} />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  {prov.key === "stripe" && "Ottieni le chiavi da https://dashboard.stripe.com/apikeys"}
                  {prov.key === "paypal" && "Ottieni Client ID e Secret da https://developer.paypal.com/dashboard/applications"}
                  {prov.key === "klarna" && "Ottieni username/password da https://playground.klarna.com (test) o https://merchants.klarna.com (live)"}
                </p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="email" className="mt-6 space-y-6">
            <div className="bg-white border border-border rounded-sm p-6" data-testid="email-resend">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg">Resend (consigliato)</h3>
                <Switch data-testid="email-resend-enabled"
                  checked={!!s.email_config.resend?.enabled}
                  onCheckedChange={(v) => setEmail("resend", "enabled", v)} />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="API Key Resend" value={s.email_config.resend?.api_key} type="password"
                  onChange={(v) => setEmail("resend", "api_key", v)} testid="email-resend-key" placeholder="re_…" />
                <Field label="Email mittente verificata" value={s.email_config.resend?.from_email}
                  onChange={(v) => setEmail("resend", "from_email", v)} testid="email-resend-from"
                  placeholder="noreply@miosito.it" />
                <Field label="Nome mittente" value={s.email_config.resend?.from_name}
                  onChange={(v) => setEmail("resend", "from_name", v)} testid="email-resend-name" />
                <Field label="Email reply-to" value={s.email_config.resend?.reply_to}
                  onChange={(v) => setEmail("resend", "reply_to", v)} testid="email-resend-replyto" />
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Ottieni la API key da https://resend.com/api-keys e verifica il dominio mittente in https://resend.com/domains
              </p>
            </div>
          </TabsContent>

          <TabsContent value="shipping" className="mt-6 space-y-6">
            <div className="bg-white border border-border rounded-sm p-6" data-testid="shipping-config">
              <h3 className="font-heading font-bold text-lg mb-4">Configurazione spedizioni</h3>
              <Field label="Spedizione gratuita sopra (€)" type="number"
                value={s.shipping_config.free_shipping_above}
                onChange={(v) => setShipping("free_shipping_above", parseFloat(v) || 0)} testid="shipping-free-above" />

              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <label className="overline text-muted-foreground">Corrieri configurati</label>
                  <button data-testid="shipping-add-carrier" type="button" onClick={() => setShipping("carriers", [
                    ...(s.shipping_config.carriers || []),
                    { name: "GLS", tracking_url_template: "https://gls-italy.com/it/per-i-clienti/cerca-spedizioni?match={tracking}", default_cost: 9.90 }
                  ])} className="text-xs px-3 h-8 rounded-sm bg-brand text-white">+ Aggiungi corriere</button>
                </div>
                <div className="space-y-2">
                  {(s.shipping_config.carriers || []).map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`shipping-carrier-${i}`}>
                      <input className="col-span-3 h-9 px-2 rounded-sm border border-border text-sm" placeholder="Nome"
                        value={c.name || ""}
                        onChange={(e) => { const arr = [...s.shipping_config.carriers]; arr[i] = { ...arr[i], name: e.target.value }; setShipping("carriers", arr); }} />
                      <input className="col-span-6 h-9 px-2 rounded-sm border border-border text-sm" placeholder="Tracking URL template (usa {tracking})"
                        value={c.tracking_url_template || ""}
                        onChange={(e) => { const arr = [...s.shipping_config.carriers]; arr[i] = { ...arr[i], tracking_url_template: e.target.value }; setShipping("carriers", arr); }} />
                      <input className="col-span-2 h-9 px-2 rounded-sm border border-border text-sm" type="number" placeholder="Costo €"
                        value={c.default_cost ?? ""}
                        onChange={(e) => { const arr = [...s.shipping_config.carriers]; arr[i] = { ...arr[i], default_cost: parseFloat(e.target.value) || 0 }; setShipping("carriers", arr); }} />
                      <button type="button" data-testid={`shipping-carrier-remove-${i}`}
                        onClick={() => { const arr = s.shipping_config.carriers.filter((_, idx) => idx !== i); setShipping("carriers", arr); }}
                        className="col-span-1 h-9 text-xs text-red-700 hover:bg-red-50 rounded-sm">Rimuovi</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="legal" className="mt-6 space-y-5 bg-white border border-border rounded-sm p-6">
            <p className="text-sm text-muted-foreground">Questi dati popolano automaticamente le pagine <b>Privacy Policy</b>, <b>Cookie Policy</b> e <b>Termini & Condizioni</b> generate dai template ufficiali per ognuno dei tuoi clienti.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Ragione sociale" value={s.legal_info.company_name} onChange={(v) => setLegal("company_name", v)} testid="legal-company" />
              <Field label="P.IVA / VAT" value={s.legal_info.vat_number} onChange={(v) => setLegal("vat_number", v)} testid="legal-vat" />
              <Field label="Indirizzo sede" value={s.legal_info.address} onChange={(v) => setLegal("address", v)} testid="legal-address" />
              <Field label="Email contatto DPO" value={s.legal_info.contact_email} onChange={(v) => setLegal("contact_email", v)} testid="legal-email" />
              <Field label="Telefono" value={s.legal_info.phone} onChange={(v) => setLegal("phone", v)} testid="legal-phone" />
              <Field label="REA" value={s.legal_info.rea} onChange={(v) => setLegal("rea", v)} testid="legal-rea" />
            </div>
          </TabsContent>

          <TabsContent value="social" className="mt-6 space-y-3 bg-white border border-border rounded-sm p-6">
            {["facebook", "instagram", "linkedin", "tiktok", "youtube", "x_twitter"].map((p) => (
              <Field key={p} label={p.replace("_", "/")} value={s.social_links[p]} onChange={(v) => setSocial(p, v)} testid={`social-${p}`} placeholder={`https://${p === "x_twitter" ? "x.com" : p + ".com"}/...`} />
            ))}
          </TabsContent>

          <TabsContent value="domain" className="mt-6 space-y-4">
            <div className="bg-white border border-border rounded-sm p-6">
              <h2 className="font-heading font-bold text-lg mb-2">Dominio personalizzato</h2>
              <p className="text-sm text-muted-foreground mb-4">Collega un tuo dominio (es. <code className="px-1 bg-accent/40 rounded">mio-negozio.it</code>) alla vetrina. Cloudflare for SaaS emette automaticamente il certificato SSL.</p>
              <CustomDomainManager />
            </div>
            <div className="bg-accent/20 border border-border rounded-sm p-5 text-sm space-y-2">
              <h3 className="font-semibold">Come configurare il tuo dominio</h3>
              <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
                <li>Inserisci il dominio sopra (es. <code>shop.miosito.it</code>) e clicca <b>Collega</b>.</li>
                <li>Vai sul pannello DNS del registrar (Aruba, GoDaddy, Cloudflare Registrar, ecc.) e crea il record CNAME indicato.</li>
                <li>Attendi 5-30 minuti: la pagina si aggiorna automaticamente. Quando vedi il pallino verde &quot;Attivo&quot;, il tuo dominio è online con SSL.</li>
                <li>Se dopo 1 ora non si attiva, verifica che il CNAME punti esattamente al valore indicato e che nessun altro record A/AAAA sia in conflitto.</li>
              </ol>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}


function NetworkCoverEditor({ networkId }) {
  const [cover, setCover] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await import("@/lib/api").then((m) => m.api.get("/public/networks"));
        const me = (data || []).find((n) => n.id === networkId);
        if (!cancel) setCover(me?.cover_image_url || "");
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [networkId]);

  const save = async (newUrl) => {
    setSaving(true);
    try {
      const { api } = await import("@/lib/api");
      await api.put(`/networks/${networkId}/cover`, { cover_image_url: newUrl || "" });
      setCover(newUrl || "");
      const { toast } = await import("sonner");
      toast.success(newUrl ? "Cover network aggiornata" : "Cover network rimossa");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error("Errore: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-xs text-muted-foreground">Carico cover network…</div>;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-sm p-4 space-y-3" data-testid="network-cover-editor">
      <div>
        <div className="overline text-amber-900 mb-1">Cover del tuo network (homepage TotalKlick Hub)</div>
        <p className="text-xs text-amber-900/80">
          Questa è la foto grande che appare nella landing principale per il tuo network. Dimensioni consigliate: <strong>1600×900px (16:9)</strong>.
        </p>
      </div>
      <ImageUploader value={cover} onChange={save} label="" testid="network-cover" />
      {saving && <div className="text-[10px] text-amber-700">Salvataggio…</div>}
    </div>
  );
}
